/**
 * @fileoverview Branded types and utility types for type-safe ID handling and
 * common schema patterns. Provides compile-time safety against mixing different
 * entity IDs.
 */

import { z } from 'zod';

// =============================================================================
// Branded ID Types
// =============================================================================

/**
 * Brand symbol for nominal typing
 */
declare const __brand: unique symbol;

/**
 * Branded type helper - creates nominal types from primitives.
 * Exported so consumers can define additional branded types if needed.
 */
export type Brand<T, B extends string> = T & { readonly [__brand]: B };

/**
 * Entity ID types - prevents accidentally passing wrong ID type
 * @example
 * ```ts
 * function getNote(id: NoteId): Promise<Note> { ... }
 * const personId: PersonId = 'pe12345' as PersonId;
 * getNote(personId); // ❌ Type error: PersonId not assignable to NoteId
 * ```
 */
export type NoteId = Brand<string, 'NoteId'>;
export type PersonId = Brand<string, 'PersonId'>;
export type CategoryId = Brand<string, 'CategoryId'>;
export type SubcategoryId = Brand<string, 'SubcategoryId'>;
export type KindId = Brand<string, 'KindId'>;
export type StatusId = Brand<string, 'StatusId'>;
export type ProjectId = Brand<string, 'ProjectId'>;
export type ResourceId = Brand<string, 'ResourceId'>;
export type AccountId = Brand<string, 'AccountId'>;
export type TagId = Brand<string, 'TagId'>;
export type AreaId = Brand<string, 'AreaId'>;
export type OrganisationId = Brand<string, 'OrganisationId'>;
export type WeightId = Brand<string, 'WeightId'>;
export type CalorieId = Brand<string, 'CalorieId'>;
export type NoteTagId = Brand<string, 'NoteTagId'>;
export type NoteVisitId = Brand<string, 'NoteVisitId'>;
export type EmbeddingId = Brand<string, 'EmbeddingId'>;
export type HistoryId = Brand<string, 'HistoryId'>;
export type LoginId = Brand<string, 'LoginId'>;
export type SpeedDialItemId = Brand<string, 'SpeedDialItemId'>;
export type AiPromptId = Brand<string, 'AiPromptId'>;
export type AttachmentId = Brand<string, 'AttachmentId'>;
export type EmailImportId = Brand<string, 'EmailImportId'>;

// =============================================================================
// Branded ID Factory
// =============================================================================

/**
 * Casts a raw string to a branded ID type with optional pattern validation.
 * Branded IDs prevent accidentally passing the wrong entity ID at compile time.
 *
 * @param id - Raw string to brand
 * @param pattern - Optional regex to validate the ID format at runtime
 * @returns Branded ID of the specified type
 * @throws Error if a pattern is provided and the ID doesn't match
 *
 * @example
 * ```ts
 * const noteId = createBrandedId<NoteId>('2abcd', ID_PATTERNS.NOTE);
 * const personId = createBrandedId<PersonId>('pe12345', ID_PATTERNS.PERSON);
 * getNote(personId); // Type error: PersonId not assignable to NoteId
 * ```
 */
export function createBrandedId<T extends string>(id: string, pattern?: RegExp): T {
  if (pattern && !pattern.test(id)) {
    throw new Error(`Invalid branded ID: "${id}" does not match expected pattern ${pattern}`);
  }
  return id as T;
}

// =============================================================================
// ID Format Constants
// =============================================================================

/**
 * Nanoid-safe character set excluding ambiguous characters (l, o, 0, 1).
 * Single source of truth used by all ID patterns.
 */
export const NANOID_CHARS = 'abcdefghijkmnpqrstuvwxyz23456789';

/**
 * Builds a prefixed nanoid7 ID pattern: prefix + digit 2-9 + 4 nanoid chars.
 * Most entity IDs follow this format.
 */
function prefixedNanoid7(prefix: string): RegExp {
  return new RegExp(`^${prefix}[2-9][${NANOID_CHARS}]{4}$`);
}

/**
 * ID format patterns for each entity type.
 * Standard entities use `prefixedNanoid7`; special formats are inline.
 */
export const ID_PATTERNS = {
  NOTE: new RegExp(`^[2-9][${NANOID_CHARS}]{4}$`),
  TAG: prefixedNanoid7('ta'),
  PERSON: /^pe[a-z0-9]{5,6}$/,
  AREA: prefixedNanoid7('ar'),
  CATEGORY: prefixedNanoid7('ca'),
  SUBCATEGORY: prefixedNanoid7('sc'),
  KIND: prefixedNanoid7('ki'),
  STATUS: prefixedNanoid7('st'),
  ORGANISATION: prefixedNanoid7('or'),
  PROJECT: prefixedNanoid7('pr'),
  RESOURCE: prefixedNanoid7('re'),
  WEIGHT: prefixedNanoid7('we'),
  CALORIE: prefixedNanoid7('kc'),
  NOTE_TAG: prefixedNanoid7('nt'),
  NOTE_VISIT: prefixedNanoid7('nv'),
  EMBEDDING: prefixedNanoid7('em'),
  HISTORY: prefixedNanoid7('hi'),
  ACCOUNT: prefixedNanoid7('ac'),
  LOGIN: prefixedNanoid7('lo'),
  SPEED_DIAL_ITEM: prefixedNanoid7('sd'),
  AI_PROMPT: prefixedNanoid7('ap'),
  ATTACHMENT: prefixedNanoid7('at'),
  EMAIL_IMPORT: prefixedNanoid7('ei'),
  BASE: /^[a-z]{2}[a-z0-9]{6}$/,
  FOREIGN_KEY: /^[a-z]{2}[a-z0-9]{5,6}$/,
} as const;

/**
 * Union of all valid ID_PATTERNS keys for type-safe pattern lookups.
 */
export type IdPatternKey = keyof typeof ID_PATTERNS;

// =============================================================================
// Enum Types (Zod schemas as single source of truth)
// =============================================================================

/**
 * Status category enum schema and type.
 * Single source of truth - import and use in StatusSchema.
 */
export const StatusCategoryEnum = z.enum(['new', 'indeterminate', 'done']);
export type StatusCategory = z.infer<typeof StatusCategoryEnum>;

/**
 * Priority enum schema and type shared by note and project entities.
 * Matches the PostgreSQL `priority` enum.
 */
export const PriorityEnum = z.enum(['Very Low', 'Low', 'Medium', 'High', 'Very High', 'Critical']);
export type Priority = z.infer<typeof PriorityEnum>;

/**
 * Numeric weights for deterministic priority sorting (lower = higher priority).
 * Use with `PRIORITY_SORT_WEIGHT[a.priority] - PRIORITY_SORT_WEIGHT[b.priority]`.
 */
export const PRIORITY_SORT_WEIGHT: Readonly<Record<Priority, number>> = {
  'Critical': 1,
  'Very High': 2,
  'High': 3,
  'Medium': 4,
  'Low': 5,
  'Very Low': 6,
};

/**
 * Embedding status enum schema and type.
 * Single source of truth - import and use in EmbeddingSchema.
 */
export const EmbeddingStatusEnum = z.enum(['pending', 'processing', 'completed', 'failed']);
export type EmbeddingStatus = z.infer<typeof EmbeddingStatusEnum>;

/**
 * Account type enum schema and type.
 * Single source of truth - import and use in AccountSchema.
 */
export const AccountTypeEnum = z.enum(['human', 'bot', 'service']);
export type AccountType = z.infer<typeof AccountTypeEnum>;
export const ACCOUNT_TYPE_VALUES = AccountTypeEnum.options;

/**
 * Auth provider enum schema and type.
 * Single source of truth - import and use in AccountSchema.
 */
export const AuthProviderEnum = z.enum(['github', 'google', 'email']);
export type AuthProvider = z.infer<typeof AuthProviderEnum>;
export const AUTH_PROVIDER_VALUES = AuthProviderEnum.options;

/**
 * Embedding status object for convenient access to values.
 */
/**
 * Email import status enum schema and type.
 * Single source of truth - import and use in EmailImportSchema.
 */
export const EmailImportStatusEnum = z.enum(['pending', 'processing', 'completed', 'failed']);
export type EmailImportStatus = z.infer<typeof EmailImportStatusEnum>;

export const EMBEDDING_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const satisfies Record<string, EmbeddingStatus>;

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Standard API success response
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

/**
 * Standard API error response
 */
export interface ApiError {
  error: string;
  code: string;
  details?: Record<string, string[]>;
}

/**
 * Paginated API response
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// =============================================================================
// Validation Result Types
// =============================================================================

/**
 * Validation success result
 */
export interface ValidationSuccess<T> {
  success: true;
  data: T;
}

/**
 * Validation error result
 */
export interface ValidationError {
  success: false;
  error: z.ZodError;
}

/**
 * Validation result union type
 */
export type ValidationResult<T> = ValidationSuccess<T> | ValidationError;
